import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Trash2, Briefcase } from 'lucide-react';
import {
  PayrollPosition,
  PayrollDepartment,
  PayrollStatus,
  PayrollSettings,
  PayType,
  CompChangeKind,
  PayrollScheduledRaise,
  PAYROLL_DEPARTMENTS,
  PAYROLL_DEPARTMENT_LABELS,
  PAYROLL_STATUSES,
  PAYROLL_STATUS_LABELS,
  PAY_TYPE_LABELS,
  DEFAULT_PAYROLL_SETTINGS,
  DEFAULT_HOURS_PER_WEEK,
  WEEKS_PER_YEAR,
} from '../../types/finances';
import { CRMUser } from '../../types';
import { isPayrollLinkableCrmUser } from '../../utils/userRoles';
import { positionMonthlyCost } from '../../utils/financesForecast';
import {
  newScheduledRaiseId,
  nextScheduledRaise,
  projectRaiseComp,
} from '../../utils/payrollCompSchedule';
import { resolveWorkState, US_STATE_CODES } from '../../utils/payrollEmployerTax';
import PayrollCostBreakdown from './PayrollCostBreakdown';
import { getJobPostingByPayrollPositionId } from '../../utils/hiringFirestore';

interface PayrollEditModalProps {
  initial: Partial<PayrollPosition> | null;
  crmUsers: CRMUser[];
  settings?: PayrollSettings;
  onClose: () => void;
  onSave: (data: Partial<PayrollPosition>) => Promise<void>;
  onDelete?: () => Promise<void>;
}

function toInputDate(d: Date | null | undefined): string {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fromInputDate(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatDate(d: Date | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);

const PayrollEditModal: React.FC<PayrollEditModalProps> = ({
  initial,
  crmUsers,
  settings = DEFAULT_PAYROLL_SETTINGS,
  onClose,
  onSave,
  onDelete,
}) => {
  const navigate = useNavigate();
  const isEdit = !!initial?.id;
  const hasLegacy = initial?.benefitsAndTaxRate != null;

  const [title, setTitle] = useState(initial?.title ?? '');
  const [department, setDepartment] = useState<PayrollDepartment>(initial?.department ?? 'shop');
  const [status, setStatus] = useState<PayrollStatus>(initial?.status ?? 'filled');
  const [payType, setPayType] = useState<PayType>(initial?.payType ?? 'salary');
  const [annualSalary, setAnnualSalary] = useState<string>(
    initial?.annualSalary != null ? String(initial.annualSalary) : '',
  );
  const [hourlyRate, setHourlyRate] = useState<string>(
    initial?.hourlyRate != null ? String(initial.hourlyRate) : '',
  );
  const [hoursPerWeek, setHoursPerWeek] = useState<string>(
    initial?.hoursPerWeek != null ? String(initial.hoursPerWeek) : String(DEFAULT_HOURS_PER_WEEK),
  );
  const [healthMonthly, setHealthMonthly] = useState<string>(
    initial?.healthReimbursementMonthly != null
      ? String(initial.healthReimbursementMonthly)
      : String(settings.defaultHealthReimbursementMonthly ?? 0),
  );
  const [ptoDays, setPtoDays] = useState<string>(
    initial?.ptoDaysPerYear != null
      ? String(initial.ptoDaysPerYear)
      : String(settings.defaultPtoDaysPerYear ?? 10),
  );
  const [extras, setExtras] = useState<string>(
    initial?.monthlyEmployeeExtras != null
      ? String(initial.monthlyEmployeeExtras)
      : String(settings.defaultMonthlyEmployeeExtras ?? 0),
  );
  const [extrasNote, setExtrasNote] = useState(initial?.monthlyEmployeeExtrasNote ?? '');

  // SEP override
  const [sepOverride, setSepOverride] = useState<boolean>(
    initial?.sepIraRateOverride != null && initial.sepIraRateOverride !== undefined,
  );
  const [sepPct, setSepPct] = useState<string>(
    initial?.sepIraRateOverride != null
      ? String((initial.sepIraRateOverride * 100).toFixed(2))
      : String((settings.sepIraRate * 100).toFixed(2)),
  );

  // Employer tax override
  const [taxOverride, setTaxOverride] = useState<boolean>(
    initial?.employerTaxRateOverride != null && initial.employerTaxRateOverride !== undefined,
  );
  const [taxPct, setTaxPct] = useState<string>(
    initial?.employerTaxRateOverride != null
      ? String((initial.employerTaxRateOverride * 100).toFixed(2))
      : String((settings.employerTaxRate * 100).toFixed(2)),
  );

  const [startDate, setStartDate] = useState<string>(toInputDate(initial?.startDate ?? new Date()));
  const [endDate, setEndDate] = useState<string>(toInputDate(initial?.endDate ?? null));
  const [crmUserId, setCrmUserId] = useState<string>(initial?.crmUserId ?? '');
  const [workState, setWorkState] = useState<string>(() => {
    if (initial?.workState) return initial.workState;
    if (initial?.crmUserId) {
      const user = crmUsers.find(u => u.uid === initial.crmUserId);
      return resolveWorkState(null, user?.location, settings.defaultWorkState ?? 'VA');
    }
    return settings.defaultWorkState ?? 'VA';
  });
  const workStateTouched = useRef(!!initial?.workState);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [linkedPostingId, setLinkedPostingId] = useState<string | null>(null);

  useEffect(() => {
    if (!initial?.id) {
      setLinkedPostingId(null);
      return;
    }
    void getJobPostingByPayrollPositionId(initial.id).then(p => {
      setLinkedPostingId(p?.id || null);
    });
  }, [initial?.id]);
  const [isActive, setIsActive] = useState<boolean>(initial?.isActive ?? true);
  const [scheduledRaises, setScheduledRaises] = useState<PayrollScheduledRaise[]>(
    () => initial?.scheduledRaises ?? [],
  );
  const [compPeriods] = useState(() => initial?.compPeriods ?? []);
  const [raiseEffectiveDate, setRaiseEffectiveDate] = useState('');
  const [raiseKind, setRaiseKind] = useState<CompChangeKind>('percent');
  const [raiseAmount, setRaiseAmount] = useState('');
  const [raiseLabel, setRaiseLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const employeeOptions = useMemo(() => {
    return crmUsers
      .filter(isPayrollLinkableCrmUser)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [crmUsers]);

  const linkedUser = useMemo(
    () => (crmUserId ? crmUsers.find(u => u.uid === crmUserId) ?? null : null),
    [crmUserId, crmUsers],
  );

  const locationParseWarning = useMemo(() => {
    if (!linkedUser?.location?.trim()) return null;
    const resolved = resolveWorkState(workState, linkedUser.location, settings.defaultWorkState ?? 'VA');
    if (!linkedUser.location.match(/\b[A-Z]{2}\b/) && resolved === (settings.defaultWorkState ?? 'VA')) {
      return `Could not parse state from "${linkedUser.location}" — using ${resolved}. Set work state manually if needed.`;
    }
    return null;
  }, [linkedUser, workState, settings.defaultWorkState]);

  const numericHourly = useMemo(() => {
    const v = parseFloat(hourlyRate);
    return Number.isFinite(v) ? Math.max(0, v) : 0;
  }, [hourlyRate]);

  const numericHoursPerWeek = useMemo(() => {
    const v = parseFloat(hoursPerWeek);
    return Number.isFinite(v) ? Math.max(0, v) : 0;
  }, [hoursPerWeek]);

  const numericSalary = useMemo(() => {
    if (payType === 'hourly') {
      return numericHourly * numericHoursPerWeek * WEEKS_PER_YEAR;
    }
    const v = parseFloat(annualSalary);
    return Number.isFinite(v) ? Math.max(0, v) : 0;
  }, [payType, annualSalary, numericHourly, numericHoursPerWeek]);

  const numericHealth = useMemo(() => {
    const v = parseFloat(healthMonthly);
    return Number.isFinite(v) ? Math.max(0, v) : 0;
  }, [healthMonthly]);

  const numericPto = useMemo(() => {
    const v = parseFloat(ptoDays);
    return Number.isFinite(v) ? Math.max(0, Math.round(v)) : 0;
  }, [ptoDays]);

  const numericExtras = useMemo(() => {
    const v = parseFloat(extras);
    return Number.isFinite(v) ? Math.max(0, v) : 0;
  }, [extras]);

  const sepRateForPreview = useMemo(() => {
    if (!sepOverride) return settings.sepIraRate;
    const v = parseFloat(sepPct);
    return Number.isFinite(v) ? Math.max(0, v) / 100 : 0;
  }, [sepOverride, sepPct, settings.sepIraRate]);

  const taxRateForPreview = useMemo(() => {
    if (!taxOverride) return settings.employerTaxRate;
    const v = parseFloat(taxPct);
    return Number.isFinite(v) ? Math.max(0, v) / 100 : 0;
  }, [taxOverride, taxPct, settings.employerTaxRate]);

  const breakdown = useMemo(() => {
    const draft: PayrollPosition = {
      id: 'preview',
      title,
      department,
      status,
      payType,
      annualSalary: numericSalary,
      hourlyRate: payType === 'hourly' ? numericHourly : null,
      hoursPerWeek: payType === 'hourly' ? numericHoursPerWeek : null,
      healthReimbursementMonthly: numericHealth,
      ptoDaysPerYear: numericPto,
      monthlyEmployeeExtras: numericExtras,
      monthlyEmployeeExtrasNote: extrasNote || undefined,
      sepIraRateOverride: sepOverride ? sepRateForPreview : null,
      employerTaxRateOverride: taxOverride ? taxRateForPreview : null,
      startDate: fromInputDate(startDate) ?? new Date(),
      endDate: fromInputDate(endDate),
      crmUserId: crmUserId || null,
      workState: workState || null,
      isActive: true,
      scheduledRaises,
      compPeriods,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'preview',
    };
    return positionMonthlyCost(draft, new Date(), settings, {
      crmUserLocation: linkedUser?.location,
    });
  }, [
    title, department, status,
    payType, numericSalary, numericHourly, numericHoursPerWeek,
    numericHealth, numericPto, numericExtras, extrasNote,
    sepOverride, sepRateForPreview, taxOverride, taxRateForPreview,
    startDate, endDate, crmUserId, workState, linkedUser?.location, settings,
    scheduledRaises, compPeriods,
  ]);

  const previewPosition = useMemo((): PayrollPosition => ({
    id: initial?.id || 'preview',
    title,
    department,
    status,
    payType,
    annualSalary: numericSalary,
    hourlyRate: payType === 'hourly' ? numericHourly : null,
    hoursPerWeek: payType === 'hourly' ? numericHoursPerWeek : null,
    healthReimbursementMonthly: numericHealth,
    ptoDaysPerYear: numericPto,
    monthlyEmployeeExtras: numericExtras,
    monthlyEmployeeExtrasNote: extrasNote || undefined,
    sepIraRateOverride: sepOverride ? sepRateForPreview : null,
    employerTaxRateOverride: taxOverride ? taxRateForPreview : null,
    startDate: fromInputDate(startDate) ?? new Date(),
    endDate: fromInputDate(endDate),
    crmUserId: crmUserId || null,
    workState: workState || null,
    notes: notes || undefined,
    isActive,
    scheduledRaises,
    compPeriods,
    createdAt: initial?.createdAt ?? new Date(),
    updatedAt: new Date(),
    createdBy: initial?.createdBy ?? 'preview',
  }), [
    initial?.id, initial?.createdAt, initial?.createdBy, title, department, status,
    payType, numericSalary, numericHourly, numericHoursPerWeek,
    numericHealth, numericPto, numericExtras, extrasNote,
    sepOverride, sepRateForPreview, taxOverride, taxRateForPreview,
    startDate, endDate, crmUserId, workState, notes, isActive,
    scheduledRaises, compPeriods,
  ]);

  const upcomingRaise = useMemo(() => nextScheduledRaise(previewPosition), [previewPosition]);

  const postRaiseBreakdown = useMemo(() => {
    if (!upcomingRaise) return null;
    const projected = projectRaiseComp(previewPosition, upcomingRaise);
    const nextPosition: PayrollPosition = {
      ...previewPosition,
      annualSalary: projected.annualSalary,
      hourlyRate: projected.hourlyRate,
    };
    const month = new Date(upcomingRaise.effectiveDate);
    return {
      raise: upcomingRaise,
      cost: positionMonthlyCost(nextPosition, month, settings, {
        crmUserLocation: linkedUser?.location,
      }).total,
    };
  }, [previewPosition, upcomingRaise, settings, linkedUser?.location]);

  const raiseDraftPreview = useMemo(() => {
    const amount = parseFloat(raiseAmount);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    if (raiseKind === 'set' && payType === 'hourly') return null;

    const projected = projectRaiseComp(previewPosition, { kind: raiseKind, amount });
    const effective = fromInputDate(raiseEffectiveDate);
    const previewMonth = effective ?? new Date();
    const nextPosition: PayrollPosition = {
      ...previewPosition,
      annualSalary: projected.annualSalary,
      hourlyRate: projected.hourlyRate,
    };
    const loadedAfter = positionMonthlyCost(nextPosition, previewMonth, settings, {
      crmUserLocation: linkedUser?.location,
    }).total;
    const loadedBefore = positionMonthlyCost(previewPosition, previewMonth, settings, {
      crmUserLocation: linkedUser?.location,
    }).total;

    return {
      annualSalary: projected.annualSalary,
      hourlyRate: projected.hourlyRate,
      loadedAfter,
      loadedDelta: loadedAfter - loadedBefore,
      salaryDelta: projected.annualSalary - numericSalary,
    };
  }, [
    raiseAmount,
    raiseKind,
    raiseEffectiveDate,
    previewPosition,
    payType,
    numericSalary,
    settings,
    linkedUser?.location,
  ]);

  const sortedHistory = useMemo(() => {
    return [...compPeriods].sort(
      (a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime(),
    );
  }, [compPeriods]);

  const sortedScheduledRaises = useMemo(() => {
    return [...scheduledRaises].sort(
      (a, b) => new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime(),
    );
  }, [scheduledRaises]);

  const addScheduledRaise = () => {
    const effective = fromInputDate(raiseEffectiveDate);
    const amount = parseFloat(raiseAmount);
    if (!effective) {
      setError('Raise effective date is required.');
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (effective < today) {
      setError('Raise effective date must be today or in the future.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Enter a valid raise amount.');
      return;
    }
    if (raiseKind === 'set' && payType === 'hourly') {
      setError('Use percent or dollar raise for hourly roles, or update hourly rate directly.');
      return;
    }
    setScheduledRaises(prev => [
      ...prev,
      {
        id: newScheduledRaiseId(),
        effectiveDate: effective,
        kind: raiseKind,
        amount,
        label: raiseLabel.trim() || undefined,
        createdAt: new Date(),
      },
    ]);
    setRaiseEffectiveDate('');
    setRaiseAmount('');
    setRaiseLabel('');
    setError(null);
  };

  const removeScheduledRaise = (id: string) => {
    setScheduledRaises(prev => prev.filter(r => r.id !== id));
  };

  const formatRaiseSummary = (raise: PayrollScheduledRaise): string => {
    if (raise.kind === 'percent') return `${raise.amount}%`;
    if (raise.kind === 'dollar') return formatCurrency(raise.amount);
    return formatCurrency(raise.amount) + ' salary';
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    if (payType === 'hourly') {
      if (numericHourly <= 0) { setError('Hourly rate must be greater than zero'); return; }
      if (numericHoursPerWeek <= 0) { setError('Hours per week must be greater than zero'); return; }
    } else if (numericSalary <= 0) {
      setError('Annual salary must be greater than zero'); return;
    }
    const startParsed = fromInputDate(startDate);
    if (!startParsed) { setError('Start date is required'); return; }
    setError(null);
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        department,
        status,
        payType,
        annualSalary: numericSalary,
        hourlyRate: payType === 'hourly' ? numericHourly : null,
        hoursPerWeek: payType === 'hourly' ? numericHoursPerWeek : null,
        healthReimbursementMonthly: numericHealth,
        ptoDaysPerYear: numericPto,
        monthlyEmployeeExtras: numericExtras,
        monthlyEmployeeExtrasNote: extrasNote.trim() || undefined,
        sepIraRateOverride: sepOverride ? sepRateForPreview : null,
        employerTaxRateOverride: taxOverride ? taxRateForPreview : null,
        startDate: startParsed,
        endDate: fromInputDate(endDate),
        crmUserId: status === 'filled' && crmUserId ? crmUserId : null,
        workState: workState.trim() || null,
        notes: notes.trim() || undefined,
        isActive,
        scheduledRaises,
        compPeriods: compPeriods.length ? compPeriods : undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to save position');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (!window.confirm('Delete this position? This cannot be undone.')) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete position');
      setSaving(false);
    }
  };

  return (
    <div className="fin-modal-overlay" onClick={onClose}>
      <div
        className="fin-modal fin-modal--wide"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit position' : 'New position'}
        style={{ width: 640 }}
      >
        <div className="fin-modal-header">
          <h3>{isEdit ? 'Edit position' : 'New position'}</h3>
          <button className="fin-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="fin-modal-body">
          {error && <div className="fin-modal-error">{error}</div>}

          {hasLegacy && (
            <div className="fin-legacy-banner">
              <span>
                <strong>Legacy burden % detected.</strong> The old{' '}
                {Math.round((initial?.benefitsAndTaxRate ?? 0) * 100)}% flat rate is
                mapped to <em>Employer tax %</em> until you confirm. Please review the
                itemized fields below — health, PTO, SEP IRA, extras — then save.
              </span>
            </div>
          )}

          <fieldset className="fin-modal-fieldset">
            <legend>Role</legend>
            <div className="fin-modal-grid">
              <div className="fin-modal-field full">
                <label className="fin-modal-label" htmlFor="position-title">Title</label>
                <input
                  id="position-title"
                  className="fin-modal-input"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g., Senior Timber Framer"
                  autoFocus
                />
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="position-dept">Department</label>
                <select
                  id="position-dept"
                  className="fin-modal-select"
                  value={department}
                  onChange={e => setDepartment(e.target.value as PayrollDepartment)}
                >
                  {PAYROLL_DEPARTMENTS.map(d => (
                    <option key={d} value={d}>{PAYROLL_DEPARTMENT_LABELS[d]}</option>
                  ))}
                </select>
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="position-status">Status</label>
                <select
                  id="position-status"
                  className="fin-modal-select"
                  value={status}
                  onChange={e => setStatus(e.target.value as PayrollStatus)}
                >
                  {PAYROLL_STATUSES.map(s => (
                    <option key={s} value={s}>{PAYROLL_STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="position-start">Start date</label>
                <input
                  id="position-start"
                  className="fin-modal-input"
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="position-end">End date (optional)</label>
                <input
                  id="position-end"
                  className="fin-modal-input"
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
              </div>

              {status === 'filled' && (
                <div className="fin-modal-field full">
                  <label className="fin-modal-label" htmlFor="position-crm-user">Linked CRM user (optional)</label>
                  <select
                    id="position-crm-user"
                    className="fin-modal-select"
                    value={crmUserId}
                    onChange={e => {
                      const uid = e.target.value;
                      setCrmUserId(uid);
                      if (!workStateTouched.current && uid) {
                        const user = crmUsers.find(u => u.uid === uid);
                        setWorkState(resolveWorkState(null, user?.location, settings.defaultWorkState ?? 'VA'));
                      }
                    }}
                  >
                    <option value="">— None —</option>
                    {employeeOptions.map(u => (
                      <option key={u.uid} value={u.uid}>
                        {u.displayName} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="position-work-state">Work state</label>
                <select
                  id="position-work-state"
                  className="fin-modal-select"
                  value={workState}
                  onChange={e => {
                    workStateTouched.current = true;
                    setWorkState(e.target.value);
                  }}
                >
                  {US_STATE_CODES.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
                <span className="fin-modal-help">
                  {linkedUser?.location
                    ? `Linked user location: ${linkedUser.location}`
                    : 'Used for state unemployment (SUTA). Defaults from linked user or company setting.'}
                </span>
                {locationParseWarning && (
                  <span className="fin-modal-help" style={{ color: '#92400E' }}>{locationParseWarning}</span>
                )}
              </div>
            </div>
          </fieldset>

          <fieldset className="fin-modal-fieldset">
            <legend>Compensation</legend>

            <div className="fin-modal-field full">
              <label className="fin-modal-label">Pay type</label>
              <div className="fin-segmented" role="group" aria-label="Pay type">
                {(['salary', 'hourly'] as PayType[]).map(t => (
                  <button
                    key={t}
                    type="button"
                    className={payType === t ? 'active' : ''}
                    aria-pressed={payType === t}
                    onClick={() => setPayType(t)}
                  >
                    {PAY_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>

            <div className="fin-modal-grid">
              {payType === 'salary' ? (
                <div className="fin-modal-field">
                  <label className="fin-modal-label" htmlFor="position-salary">Annual salary (USD)</label>
                  <input
                    id="position-salary"
                    className="fin-modal-input"
                    type="number"
                    min="0"
                    step="100"
                    value={annualSalary}
                    onChange={e => setAnnualSalary(e.target.value)}
                    placeholder="0"
                  />
                </div>
              ) : (
                <>
                  <div className="fin-modal-field">
                    <label className="fin-modal-label" htmlFor="position-hourly">Hourly rate (USD)</label>
                    <input
                      id="position-hourly"
                      className="fin-modal-input"
                      type="number"
                      min="0"
                      step="0.25"
                      value={hourlyRate}
                      onChange={e => setHourlyRate(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="fin-modal-field">
                    <label className="fin-modal-label" htmlFor="position-hours">Hours / week</label>
                    <input
                      id="position-hours"
                      className="fin-modal-input"
                      type="number"
                      min="0"
                      max="80"
                      step="1"
                      value={hoursPerWeek}
                      onChange={e => setHoursPerWeek(e.target.value)}
                    />
                    <span className="fin-modal-help">
                      {(numericHoursPerWeek * WEEKS_PER_YEAR).toLocaleString()} hrs/yr ={' '}
                      {formatCurrency(numericSalary)} annualized
                    </span>
                  </div>
                </>
              )}

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="position-health">Health reimburse $/mo</label>
                <input
                  id="position-health"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="25"
                  value={healthMonthly}
                  onChange={e => setHealthMonthly(e.target.value)}
                />
                <span className="fin-modal-help">Counted as taxable income.</span>
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="position-pto">PTO days / yr</label>
                <input
                  id="position-pto"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="1"
                  value={ptoDays}
                  onChange={e => setPtoDays(e.target.value)}
                />
                <span className="fin-modal-help">
                  Default {settings.defaultPtoDaysPerYear} days. Additive backfill: each PTO day adds one extra day of base pay per month (annual ÷ 260). Salary is still paid while out.
                </span>
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="position-extras">Monthly extras $</label>
                <input
                  id="position-extras"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="10"
                  value={extras}
                  onChange={e => setExtras(e.target.value)}
                />
                <span className="fin-modal-help">Car allowance, custom phone plan, etc.</span>
              </div>

              <div className="fin-modal-field full">
                <label className="fin-modal-label" htmlFor="position-extras-note">Extras note (optional)</label>
                <input
                  id="position-extras-note"
                  className="fin-modal-input"
                  value={extrasNote}
                  onChange={e => setExtrasNote(e.target.value)}
                  placeholder="e.g., $200/mo car allowance"
                />
              </div>
            </div>
          </fieldset>

          {isEdit && (
            <>
              <fieldset className="fin-modal-fieldset">
                <legend>Scheduled raises</legend>
                {sortedScheduledRaises.length === 0 ? (
                  <p className="fin-modal-help">No upcoming raises scheduled.</p>
                ) : (
                  <ul className="fin-raise-list">
                    {sortedScheduledRaises.map(raise => {
                      const projected = projectRaiseComp(previewPosition, raise);
                      return (
                        <li key={raise.id} className="fin-raise-list-item">
                          <div>
                            <strong>{formatDate(raise.effectiveDate)}</strong>
                            {' · '}
                            {formatRaiseSummary(raise)}
                            {raise.label ? ` · ${raise.label}` : ''}
                            <div className="fin-modal-help">
                              → {formatCurrency(projected.annualSalary)}/yr
                            </div>
                          </div>
                          <button
                            type="button"
                            className="fin-icon-btn"
                            onClick={() => removeScheduledRaise(raise.id)}
                            aria-label="Remove scheduled raise"
                          >
                            <Trash2 size={14} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="fin-modal-grid fin-raise-add-grid">
                  <div className="fin-modal-field">
                    <label className="fin-modal-label" htmlFor="raise-effective">Effective date</label>
                    <input
                      id="raise-effective"
                      className="fin-modal-input"
                      type="date"
                      value={raiseEffectiveDate}
                      onChange={e => setRaiseEffectiveDate(e.target.value)}
                    />
                  </div>
                  <div className="fin-modal-field">
                    <label className="fin-modal-label" htmlFor="raise-kind">Type</label>
                    <select
                      id="raise-kind"
                      className="fin-modal-input"
                      value={raiseKind}
                      onChange={e => setRaiseKind(e.target.value as CompChangeKind)}
                    >
                      <option value="percent">Percent %</option>
                      <option value="dollar">Dollar $/yr</option>
                      {payType === 'salary' && <option value="set">Set new salary</option>}
                    </select>
                  </div>
                  <div className="fin-modal-field">
                    <label className="fin-modal-label" htmlFor="raise-amount">Amount</label>
                    <input
                      id="raise-amount"
                      className="fin-modal-input"
                      type="number"
                      min="0"
                      step={raiseKind === 'percent' ? '0.1' : '100'}
                      value={raiseAmount}
                      onChange={e => setRaiseAmount(e.target.value)}
                      placeholder={raiseKind === 'percent' ? '3' : '5000'}
                    />
                  </div>
                  <div className="fin-modal-field">
                    <label className="fin-modal-label" htmlFor="raise-label">Label (optional)</label>
                    <input
                      id="raise-label"
                      className="fin-modal-input"
                      value={raiseLabel}
                      onChange={e => setRaiseLabel(e.target.value)}
                      placeholder="Promotion, COLA…"
                    />
                  </div>
                </div>
                {raiseDraftPreview && (
                  <div className="fin-raise-draft-preview" aria-live="polite">
                    <div className="fin-raise-draft-preview-title">Raise preview</div>
                    <p>
                      New salary:{' '}
                      <strong>{formatCurrency(raiseDraftPreview.annualSalary)}/yr</strong>
                      {raiseDraftPreview.salaryDelta !== 0 && (
                        <span className="fin-raise-draft-delta">
                          {' '}({raiseDraftPreview.salaryDelta > 0 ? '+' : ''}
                          {formatCurrency(raiseDraftPreview.salaryDelta)})
                        </span>
                      )}
                    </p>
                    {payType === 'hourly' && raiseDraftPreview.hourlyRate != null && (
                      <p className="fin-modal-help">
                        {formatCurrency(raiseDraftPreview.hourlyRate)}/hr
                      </p>
                    )}
                    <p className="fin-modal-help">
                      Loaded cost: {formatCurrency(raiseDraftPreview.loadedAfter)}/mo
                      {raiseDraftPreview.loadedDelta !== 0 && (
                        <span className="fin-raise-draft-delta">
                          {' '}({raiseDraftPreview.loadedDelta > 0 ? '+' : ''}
                          {formatCurrency(raiseDraftPreview.loadedDelta)}/mo)
                        </span>
                      )}
                    </p>
                  </div>
                )}
                <button type="button" className="fin-secondary-btn" onClick={addScheduledRaise}>
                  Add scheduled raise
                </button>
              </fieldset>

              {sortedHistory.length > 0 && (
                <fieldset className="fin-modal-fieldset">
                  <legend>Comp history</legend>
                  <ul className="fin-comp-history-list">
                    {sortedHistory.map(period => {
                      const periodPosition: PayrollPosition = {
                        ...previewPosition,
                        annualSalary: period.annualSalary,
                        hourlyRate: period.hourlyRate ?? null,
                        hoursPerWeek: period.hoursPerWeek ?? null,
                      };
                      const loaded = positionMonthlyCost(
                        periodPosition,
                        new Date(period.effectiveDate),
                        settings,
                        { crmUserLocation: linkedUser?.location },
                      ).total;
                      return (
                        <li key={period.id} className="fin-comp-history-item">
                          <div>
                            <strong>{formatDate(period.effectiveDate)}</strong>
                            {period.endDate ? ` – ${formatDate(period.endDate)}` : ' – present'}
                            {period.label ? ` · ${period.label}` : ''}
                          </div>
                          <div className="fin-modal-help">
                            {formatCurrency(period.annualSalary)}/yr · {formatCurrency(loaded)}/mo loaded
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </fieldset>
              )}
            </>
          )}

          <fieldset className="fin-modal-fieldset">
            <legend>Rates (override company defaults)</legend>
            <div className="fin-modal-grid">
              <div className="fin-modal-field">
                <label className="fin-modal-toggle">
                  <span className="fin-switch">
                    <input
                      type="checkbox"
                      checked={sepOverride}
                      onChange={e => setSepOverride(e.target.checked)}
                      aria-label="Override SEP IRA rate"
                    />
                    <span className="fin-switch-slider" />
                  </span>
                  <span>Override SEP IRA %</span>
                </label>
                {sepOverride ? (
                  <input
                    className="fin-modal-input"
                    type="number"
                    min="0"
                    step="0.25"
                    value={sepPct}
                    onChange={e => setSepPct(e.target.value)}
                    aria-label="SEP IRA % override"
                  />
                ) : (
                  <span className="fin-modal-help">
                    Company default: {(settings.sepIraRate * 100).toFixed(2)}%
                  </span>
                )}
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-toggle">
                  <span className="fin-switch">
                    <input
                      type="checkbox"
                      checked={taxOverride}
                      onChange={e => setTaxOverride(e.target.checked)}
                      aria-label="Override employer tax rate"
                    />
                    <span className="fin-switch-slider" />
                  </span>
                  <span>Override employer tax %</span>
                </label>
                {taxOverride ? (
                  <input
                    className="fin-modal-input"
                    type="number"
                    min="0"
                    step="0.05"
                    value={taxPct}
                    onChange={e => setTaxPct(e.target.value)}
                    aria-label="Employer tax % override"
                  />
                ) : (
                  <span className="fin-modal-help">
                    Itemized SS, Medicare, FUTA, and SUTA by work state (see breakdown below).
                  </span>
                )}
              </div>
            </div>
          </fieldset>

          <div className="fin-modal-field full">
            <label className="fin-modal-label" htmlFor="position-notes">Notes</label>
            <textarea
              id="position-notes"
              className="fin-modal-textarea"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional notes (planned reasoning, comp structure, etc.)"
            />
          </div>

          <div className="fin-modal-field full">
            <label className="fin-modal-toggle">
              <span className="fin-switch">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                />
                <span className="fin-switch-slider" />
              </span>
              <span>Active — include in monthly burn and forecast</span>
            </label>
          </div>

          <div className="fin-cost-breakdown" aria-live="polite">
            <div className="fin-cost-breakdown-title">
              Loaded monthly cost
              {payType === 'hourly' && numericHourly > 0 && (
                <span style={{ marginLeft: 8, fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
                  · {formatCurrency(numericHourly)}/hr × {numericHoursPerWeek} hrs/wk
                </span>
              )}
            </div>
            <PayrollCostBreakdown
              cost={breakdown}
              sepRateLabel={`${(sepRateForPreview * 100).toFixed(2)}%`}
              extrasNote={extrasNote || undefined}
              ptoDays={numericPto}
            />
            {postRaiseBreakdown && (
              <p className="fin-modal-help fin-raise-preview-line">
                After {formatDate(postRaiseBreakdown.raise.effectiveDate)}:{' '}
                <strong>{formatCurrency(postRaiseBreakdown.cost)}/mo</strong>
                {' '}(+{formatCurrency(postRaiseBreakdown.cost - breakdown.total)})
                {postRaiseBreakdown.raise.label ? ` · ${postRaiseBreakdown.raise.label}` : ''}
              </p>
            )}
          </div>
        </div>

        <div className="fin-modal-footer">
          {isEdit && onDelete && (
            <button
              type="button"
              className="fin-secondary-btn"
              style={{ color: '#991B1B', borderColor: '#FECACA' }}
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </button>
          )}
          {isEdit && initial?.id && (status === 'open' || status === 'planned') && (
            <button
              type="button"
              className="fin-secondary-btn"
              onClick={() => {
                if (linkedPostingId) {
                  navigate('/people/hiring');
                  onClose();
                  return;
                }
                const params = new URLSearchParams({
                  new: '1',
                  payrollPositionId: initial.id!,
                  title,
                  department,
                });
                navigate(`/people/hiring?${params.toString()}`);
                onClose();
              }}
            >
              <Briefcase size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {linkedPostingId ? 'View job posting' : 'Create job posting'}
            </button>
          )}
          <div className="fin-modal-footer-right">
            <button type="button" className="fin-secondary-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="fin-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add position'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollEditModal;
